import type { TealiumTags } from '../constants/tealium-tags';
import type { GALabels } from '../constants/ga-labels';
import type { GACategories } from '../constants/ga-categories';
import type { GAActions } from '../constants/ga-actions';
import type { AmplitudeProperties } from '../types';

const CALLBACK_DEFERRED_TIMER = 100;

// Tealium Payloads (
export type GAEventTealium = {
  ga_event_category: GACategories;
  ga_event_label: GALabels;
  ga_event_action: GAActions;
  [key: string]: string | undefined;
};

export type AmplitudeEventTealium = {
  amplitude_event_label: string;
  amplitude_event_properties?: AmplitudeProperties;
};

export type AmplitudeUserTealium = {
  amplitude_user_properties?: AmplitudeProperties;
  amplitude_user_properties_setOnce?: AmplitudeProperties;
};

export type TealiumPayload =
  | GAEventTealium
  | AmplitudeEventTealium
  | AmplitudeUserTealium;

declare global {
  export interface Window {
    // Tealium
    utag?: TealiumUTag;
  }
}

function onCompleteCallback(payload: TealiumPayload): void {
  const { utag } = window;
  if (utag?.data) {
    Object.keys(payload).forEach((key) => {
      utag.data[key] = undefined;
    });
  }
}

export function tealiumLink(
  payload: TealiumPayload,
  callback?: () => void,
): void {
  if (!payload || Object.keys(payload).length === 0) {
    return;
  }
  if (window?.utag?.link) {
    console.debug('sending event', payload);
  }
  window?.utag?.link(payload, () => {
    setTimeout(() => {
      onCompleteCallback(payload);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      callback && callback();
    }, CALLBACK_DEFERRED_TIMER);
  });
}

export function tealiumView(
  payload: any,
  callback?: () => void | undefined,
  tagIds?: TealiumTags[],
) {
  if (window?.utag?.view) {
    console.debug('sending view event', payload);
  }
  window.utag?.view(
    payload,
    () => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      callback && setTimeout(callback, CALLBACK_DEFERRED_TIMER);
    },
    tagIds,
  );
}

export interface TealiumUTag {
  track: (tealiumEvent: string, data: unknown) => void;
  link: (payload: unknown, callback: () => void, tags?: number[]) => void;
  view: (payload: unknown, callback: () => void, tags?: number[]) => void;
  ut: { merge: (a: unknown, b: unknown, i: number) => void };
  handler: {
    RE: (
      view: string,
      data: Record<string, unknown>,
      loadRuleOption: 'blr' | 'alr' | 'end',
    ) => void;
  };
  data: Record<string, unknown>;
  loader?: {
    cfg: Record<string, { load: () => void }>;
  };
}

export function tealiumHandler(
  ...args: Parameters<TealiumUTag['handler']['RE']>
) {
  window.utag?.handler?.RE(...args);
}

export function tealiumMerge(obj1: unknown, obj2: unknown) {
  window.utag?.ut?.merge(obj1, obj2, 1);
}
